import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { InfoTooltip } from "./InfoTooltip";
import NotificationCenter from "./NotificationCenter";
import {
  changePassword,
  getMyProfile,
  getUnreadCount,
  updateMyProfile,
  type UserProfile,
} from "../services/api";
import { useAuth } from "../services/auth-context";
import { usePageMeta } from "../services/page-context";
import { useToast } from "../services/toast-context";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

interface TopbarProps {
  canAccessApp: boolean;
  onMenuToggle?: () => void;
  menuOpen?: boolean;
}

export function Topbar({ canAccessApp, onMenuToggle, menuOpen }: TopbarProps) {
  const { tokens, logout } = useAuth();
  const navigate = useNavigate();
  const { meta } = usePageMeta();
  const { showToast } = useToast();

  // ── Notifications ────────────────────────────────────────
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const sseRef = useRef<EventSource | null>(null);

  // ── User menu ────────────────────────────────────────────
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // ── Profile modal ────────────────────────────────────────
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch user profile
  useEffect(() => {
    if (!tokens?.access_token) return;
    getMyProfile(tokens.access_token)
      .then(setProfile)
      .catch(() => {});
  }, [tokens?.access_token]);

  // SSE connection for real-time notifications
  useEffect(() => {
    if (!tokens?.access_token || !canAccessApp) return;
    const url = `${API_BASE}/notifications/stream?token=${encodeURIComponent(tokens.access_token)}`;
    const es = new EventSource(url);
    sseRef.current = es;
    es.addEventListener("notification", () => setNotifCount((c) => c + 1));
    es.onerror = () => {};
    return () => { es.close(); sseRef.current = null; };
  }, [tokens?.access_token, canAccessApp]);

  // Polling fallback for unread count
  const pollNotifications = useCallback(async () => {
    if (!tokens?.access_token || !canAccessApp) return;
    try {
      const data = await getUnreadCount(tokens.access_token);
      setNotifCount(data.unread_count);
    } catch {}
  }, [tokens?.access_token, canAccessApp]);

  useEffect(() => {
    void pollNotifications();
    const interval = setInterval(() => void pollNotifications(), 60_000);
    return () => clearInterval(interval);
  }, [pollNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openProfileModal = () => {
    setEditName(profile?.full_name ?? "");
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setProfileModalOpen(true);
    setUserMenuOpen(false);
  };

  const handleSaveProfile = async () => {
    if (!tokens?.access_token) return;
    setSaving(true);
    try {
      // Update name if changed
      if (editName !== (profile?.full_name ?? "")) {
        const updated = await updateMyProfile(tokens.access_token, {
          full_name: editName || null,
        });
        setProfile(updated);
      }

      // Change password if fields filled
      if (currentPwd && newPwd) {
        if (newPwd !== confirmPwd) {
          showToast("Le password non coincidono", "error");
          setSaving(false);
          return;
        }
        if (newPwd.length < 8) {
          showToast("La nuova password deve avere almeno 8 caratteri", "error");
          setSaving(false);
          return;
        }
        await changePassword(tokens.access_token, {
          current_password: currentPwd,
          new_password: newPwd,
        });
      }

      showToast("Profilo aggiornato", "success");
      setProfileModalOpen(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Errore nel salvataggio";
      showToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
    : profile?.email?.[0]?.toUpperCase() ?? "?";

  return (
    <>
    <div className="topbar">
      {onMenuToggle && (
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={onMenuToggle}
          aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
        >
          <span className={`hamburger ${menuOpen ? "hamburger--open" : ""}`} />
        </button>
      )}
      <div className="topbar-title-area">
        {meta.title && <h1 className="topbar-page-title">{meta.title}</h1>}
        {meta.helpText && <InfoTooltip content={meta.helpText} label={`Info ${meta.title}`} />}
      </div>

      <div className="topbar-actions">
        {/* Notification bell */}
        {canAccessApp && (
          <div className="topbar-dropdown-anchor">
            <button
              className="topbar-icon-btn"
              onClick={() => { setNotifOpen(v => !v); setUserMenuOpen(false); }}
              aria-label={`Notifiche (${notifCount} non lette)`}
            >
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {notifCount > 0 && (
                <span className="topbar-badge">{notifCount > 99 ? "99+" : notifCount}</span>
              )}
            </button>
            <NotificationCenter
              isOpen={notifOpen}
              onClose={() => setNotifOpen(false)}
              onCountChange={setNotifCount}
            />
          </div>
        )}

        {/* User menu */}
        <div className="topbar-dropdown-anchor" ref={userMenuRef}>
          <button
            className="topbar-user-btn"
            onClick={() => { setUserMenuOpen(v => !v); setNotifOpen(false); }}
            aria-label="Menu utente"
          >
            {profile?.full_name && (
              <div className="topbar-user-identity">
                <span className="topbar-user-identity-name">{profile.full_name}</span>
                <span className="topbar-user-identity-role">
                  {tokens?.role === "owner" ? "Titolare" :
                   tokens?.role === "admin" ? "Amministratore" :
                   tokens?.role === "operator" ? "Operatore" : "Visualizzatore"}
                </span>
              </div>
            )}
            <span className="topbar-avatar">{initials}</span>
          </button>

          {userMenuOpen && (
            <div className="topbar-user-dropdown">
              <div className="topbar-user-info">
                <span className="topbar-avatar topbar-avatar--lg">{initials}</span>
                <div>
                  {profile?.full_name && (
                    <div className="topbar-user-name">{profile.full_name}</div>
                  )}
                  <div className="topbar-user-email">{profile?.email ?? ""}</div>
                  <div className="topbar-user-role">
                    {tokens?.role === "owner" ? "Titolare" :
                     tokens?.role === "admin" ? "Amministratore" :
                     tokens?.role === "operator" ? "Operatore" : "Visualizzatore"}
                  </div>
                </div>
              </div>

              <div className="topbar-user-divider" />

              <button className="topbar-user-action" onClick={openProfileModal}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Il mio profilo
              </button>

              <div className="topbar-user-sublabel">Impostazioni</div>
              {[
                { label: "Generale", tab: "generale" },
                { label: "Email", tab: "email" },
                { label: "WhatsApp", tab: "whatsapp" },
                { label: "Pagamenti", tab: "pagamenti" },
              ].map((item) => (
                <button
                  key={item.tab}
                  className="topbar-user-action topbar-user-action--sub"
                  onClick={() => { navigate(`/settings?tab=${item.tab}`); setUserMenuOpen(false); }}
                >
                  {item.label}
                </button>
              ))}

              <button
                className="topbar-user-action"
                onClick={() => { navigate("/billing"); setUserMenuOpen(false); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M12 18v-6M9 15h6" />
                </svg>
                Fatturazione
              </button>

              <div className="topbar-user-divider" />

              <button className="topbar-user-action topbar-user-action--danger" onClick={logout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
                Esci
              </button>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* ── Profile Modal ──────────────────────────────────── */}
    {profileModalOpen && (
      <div className="modal-backdrop" onClick={() => setProfileModalOpen(false)}>
        <div className="modal profile-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Il mio profilo</h2>
            <button className="icon-btn" onClick={() => setProfileModalOpen(false)} aria-label="Chiudi">
              &times;
            </button>
          </div>

          <div className="profile-modal-body">
            {/* Account info */}
            <div className="profile-section">
              <h3>Dati account</h3>
              <label className="form-label">
                Email
                <input type="email" className="form-input" value={profile?.email ?? ""} disabled />
              </label>
              <label className="form-label">
                Nome completo
                <input
                  type="text"
                  className="form-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nome e cognome"
                />
              </label>
            </div>

            {/* Change password */}
            <div className="profile-section">
              <h3>Modifica password</h3>
              <p className="profile-section-hint">Lascia vuoto se non vuoi modificare la password.</p>
              <label className="form-label">
                Password attuale
                <input
                  type="password"
                  className="form-input"
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
              <label className="form-label">
                Nuova password
                <input
                  type="password"
                  className="form-input"
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Minimo 8 caratteri"
                />
              </label>
              <label className="form-label">
                Conferma nuova password
                <input
                  type="password"
                  className="form-input"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button className="ghost-btn" onClick={() => setProfileModalOpen(false)}>Annulla</button>
            <button className="primary-btn" onClick={handleSaveProfile} disabled={saving}>
              {saving ? "Salvataggio..." : "Salva modifiche"}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
